// 2D golden-hour scene — no WebGL engine needed.

const pageSelector = '[data-printale-contact-page]';

const destinations = [
  {
    id: 'poster',
    title: 'Poster Help',
    copy: 'Custom poster questions.',
    subject: 'Poster Help',
    wax: '#bf4f62',
  },
  {
    id: 'order',
    title: 'Order Support',
    copy: 'Delivery, changes, or issues.',
    subject: 'Order Support',
    wax: '#9262c6',
  },
  {
    id: 'collab',
    title: 'Collaboration',
    copy: 'Brands, creators, partners.',
    subject: 'Collaboration',
    wax: '#c7923f',
  },
  {
    id: 'general',
    title: 'General Love Note',
    copy: 'Anything else for Printale.',
    subject: 'General Love Note',
    wax: '#b94a54',
  },
];

function createDestinationChooser(formElement, subjectInput, hiddenDestination, wax) {
  const chooser = document.createElement('div');
  chooser.className = 'printale-contact-destinations';
  chooser.setAttribute('role', 'radiogroup');
  chooser.setAttribute('aria-label', 'Message destination');

  const buttons = destinations.map((destination, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'printale-contact-destination';
    button.dataset.destination = destination.id;
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', index === 0 ? 'true' : 'false');
    button.innerHTML = `
      <span class="printale-contact-destination__icon" aria-hidden="true"></span>
      <span>
        <span class="printale-contact-destination__title">${destination.title}</span>
        <span class="printale-contact-destination__copy">${destination.copy}</span>
      </span>
    `;

    button.addEventListener('click', () => {
      buttons.forEach((item) => {
        item.classList.toggle('is-selected', item === button);
        item.setAttribute('aria-checked', item === button ? 'true' : 'false');
      });

      subjectInput.value = destination.subject;
      hiddenDestination.value = destination.title;
      wax.style.setProperty('--contact-wax', destination.wax);
      formElement.dataset.destination = destination.id;
    });

    chooser.append(button);
    return button;
  });

  buttons[0]?.click();
  return chooser;
}

function updateLabel(input, heading, helper) {
  const label = input?.labels?.[0];
  if (!label) return;

  label.classList.remove('visually-hidden');
  label.classList.add('printale-contact-letter-label');
  label.innerHTML = `<span>${heading}</span><small>${helper}</small>`;
}

function enhanceContactForm(root) {
  const mount = root.querySelector('[data-printale-contact-form-mount]');
  const loading = root.querySelector('.printale-contact-letter__loading');
  const wax = root.querySelector('[data-printale-contact-wax]');
  const formRoot = document.querySelector('.contact-form');

  if (!mount || !formRoot) return;

  const sourceSection = formRoot.closest('.shopify-section');
  mount.append(formRoot);
  loading?.remove();
  sourceSection?.classList.add('printale-contact-form-source');

  const formElement = formRoot.querySelector('form');
  if (!formElement) return;

  const nameInput = formElement.querySelector('#ContactForm-name');
  const emailInput = formElement.querySelector('#ContactForm-email');
  const subjectInput = formElement.querySelector('#ContactForm-phone');
  const messageInput = formElement.querySelector('#ContactForm-body');
  const submitButton = formElement.querySelector('.submit-button');

  updateLabel(nameInput, 'From:', 'Name');
  updateLabel(emailInput, 'Return Address:', 'Email');
  updateLabel(subjectInput, 'Subject of the Scroll:', 'Destination');
  updateLabel(messageInput, 'Your Message:', 'Message');

  if (nameInput) nameInput.placeholder = 'Name';
  if (emailInput) emailInput.placeholder = 'Email';

  if (subjectInput) {
    subjectInput.type = 'text';
    subjectInput.name = 'contact[subject]';
    subjectInput.id = 'ContactForm-subject';
    subjectInput.removeAttribute('pattern');
    subjectInput.removeAttribute('autocomplete');
    subjectInput.placeholder = 'Choose a destination';
    subjectInput.readOnly = true;
  }

  if (messageInput) {
    messageInput.placeholder = 'Write your note here...';
  }

  if (submitButton) {
    submitButton.textContent = 'Send Message in a Bottle';
  }

  const hiddenDestination = document.createElement('input');
  hiddenDestination.type = 'hidden';
  hiddenDestination.name = 'contact[message_destination]';
  formElement.append(hiddenDestination);

  if (subjectInput && wax) {
    const chooser = createDestinationChooser(formElement, subjectInput, hiddenDestination, wax);
    const firstField = formElement.querySelector('.contact-form__form-row') || formElement.firstElementChild;
    formElement.insertBefore(chooser, firstField);
  }

  const success = formRoot.querySelector('.contact-form__success');
  if (success) {
    mount.classList.add('is-sent');
  }

  const sendoff = root.querySelector('[data-printale-sendoff]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SENDOFF_MS = reduceMotion ? 250 : 4300;

  formElement.addEventListener('submit', (event) => {
    if (formElement.dataset.nativeSubmit === 'true') return;
    if (!formElement.checkValidity()) return;

    event.preventDefault();

    if (sendoff) {
      sendoff.classList.add('is-playing');
      sendoff.setAttribute('aria-hidden', 'false');
    }

    window.setTimeout(() => {
      formElement.dataset.nativeSubmit = 'true';
      HTMLFormElement.prototype.submit.call(formElement);
    }, SENDOFF_MS);
  });
}

function initRiverScene2D(root) {
  const canvas = root.querySelector('[data-printale-contact-river-canvas]');
  if (!canvas) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  let width = 0;
  let height = 0;
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  let motes = [];
  let rafId = null;

  function resize() {
    const rect = canvas.getBoundingClientRect();
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const moteCount = reduceMotion ? 0 : Math.round((width * height) / 26000);
    motes = Array.from({ length: moteCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      r: 0.6 + Math.random() * 1.6,
      drift: 0.1 + Math.random() * 0.25,
      phase: Math.random() * Math.PI * 2,
      roseVariant: Math.random() < 0.3,  // 30% brand-rose colored
    }));
  }

  function drawFrame(t) {
    ctx.clearRect(0, 0, width, height);

    // --- 3 parallax wave layers ---
    const waveLayers = [
      { speed: 0.0007, count: 10, alphaBase: 0.04, alphaRange: 0.06,  widthBase: 0.5, widthRange: 0.5,  ampBase: 1,  ampRange: 2  }, // far
      { speed: 0.0010, count: 13, alphaBase: 0.07, alphaRange: 0.09,  widthBase: 0.8, widthRange: 1.0,  ampBase: 2,  ampRange: 5  }, // mid
      { speed: 0.0014, count: 16, alphaBase: 0.12, alphaRange: 0.12,  widthBase: 1.2, widthRange: 1.6,  ampBase: 4,  ampRange: 10 }, // near
    ];

    for (const layer of waveLayers) {
      const phase = t * layer.speed;
      for (let i = 0; i < layer.count; i++) {
        const p = i / (layer.count - 1);         // 0 (top) → 1 (bottom)
        const y = height * (0.03 + p * 0.94);
        const amp = layer.ampBase + p * layer.ampRange;
        const wobble = Math.sin(phase + i * 0.65) * amp;
        const alpha = layer.alphaBase + p * layer.alphaRange;
        const lw = layer.widthBase + p * layer.widthRange;

        // Halo pass (soft blur effect)
        ctx.strokeStyle = `rgba(255, 214, 180, ${(alpha * 0.4).toFixed(3)})`;
        ctx.lineWidth = lw * 2.2;
        ctx.beginPath();
        ctx.moveTo(0, y + wobble);
        ctx.bezierCurveTo(
          width * 0.28, y - wobble * 0.7,
          width * 0.62, y + wobble * 0.9,
          width, y - wobble * 0.4
        );
        ctx.stroke();

        // Crisp pass on top
        ctx.strokeStyle = `rgba(255, 214, 180, ${alpha.toFixed(3)})`;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.moveTo(0, y + wobble);
        ctx.bezierCurveTo(
          width * 0.28, y - wobble * 0.7,
          width * 0.62, y + wobble * 0.9,
          width, y - wobble * 0.4
        );
        ctx.stroke();
      }
    }

    // --- Sun-glitter column ---
    const cx = width * 0.26;
    const colOscillate = Math.sin(t * 0.0006) * 6;

    for (let i = 0; i < 24; i++) {
      const p = i / 23;
      const y = height * (0.01 + p * 0.97);

      // Brightness peaks at mid-canvas, fades at top + bottom
      const brightness = 1 - Math.abs(p - 0.45) * 1.6;
      if (brightness <= 0) continue;

      const shimmerA = Math.sin(t * 0.004 + i * 1.3);
      const shimmerB = Math.sin(t * 0.003 + i * 0.8);
      const dashW = (14 + shimmerA * 7) * (0.5 + p * 0.8);
      const dashH = 1.8 + p * 2.2;
      const alpha = brightness * (0.45 + shimmerB * 0.2);
      const jitterX = colOscillate + Math.sin(t * 0.002 + i * 0.5) * (3 + p * 6);

      ctx.fillStyle = `rgba(255, 236, 200, ${Math.max(0, alpha).toFixed(3)})`;
      ctx.fillRect(cx - dashW / 2 + jitterX, y, dashW, dashH);
    }

    // --- Drifting warm motes ---
    for (const m of motes) {
      m.x += m.drift;
      if (m.x > width + 4) m.x = -4;
      const tw = 0.5 + 0.5 * Math.sin(t * 0.002 + m.phase);
      const alpha = (0.45 * tw).toFixed(3);
      // Alternate between warm ember and brand rose
      const color = m.roseVariant
        ? `rgba(255, 160, 140, ${alpha})`
        : `rgba(255, 210, 160, ${alpha})`;
      ctx.beginPath();
      ctx.fillStyle = color;
      ctx.arc(m.x, m.y + Math.sin(t * 0.001 + m.phase) * 6, m.r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  resize();
  window.addEventListener('resize', resize);

  if (reduceMotion) {
    drawFrame(0); // single static frame
    return;
  }

  const loop = (now) => {
    drawFrame(now);
    rafId = requestAnimationFrame(loop);
  };
  rafId = requestAnimationFrame(loop);

  // Pause when tab hidden, resume when visible
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      cancelAnimationFrame(rafId);
      rafId = null;
    } else if (!rafId) {
      rafId = requestAnimationFrame(loop);
    }
  });
}

function initParticles(root) {
  // Skip on reduced-motion or coarse pointer (mobile/touch)
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.matchMedia('(pointer: coarse)').matches) return;

  const scene = root.querySelector('.printale-contact-river__scene');
  if (!scene) return;

  // Create canvas dynamically — no Liquid/HTML change needed
  const canvas = document.createElement('canvas');
  canvas.className = 'printale-contact-river__particles';
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:1;';
  scene.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  let width = 0;
  let height = 0;
  let particles = [];
  let rafId = null;

  function resize() {
    const rect = scene.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const count = Math.min(25, Math.round((width * height) / 18000));
    particles = Array.from({ length: count }, () => ({
      x: Math.random() * width * 0.6,   // left 60% only
      y: Math.random() * height,
      r: 1.5 + Math.random() * 3.0,
      vy: -(0.08 + Math.random() * 0.12),
      vx: (Math.random() - 0.5) * 0.08,
      phase: Math.random() * Math.PI * 2,
      rose: Math.random() < 0.3,         // 30% brand-rose, 70% warm ember
    }));
  }

  function drawParticles(t) {
    ctx.clearRect(0, 0, width, height);

    for (const p of particles) {
      p.y += p.vy;
      p.x += p.vx;

      // Reset particle when it drifts above canvas
      if (p.y < -p.r * 2) {
        p.y = height + p.r * 2;
        p.x = Math.random() * width * 0.6;
      }

      const pulse = 0.5 + 0.5 * Math.sin(t * 0.0015 + p.phase);
      const alpha = (0.2 + pulse * 0.45).toFixed(3);

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = p.rose
        ? `rgba(255, 150, 130, ${alpha})`
        : `rgba(255, 210, 160, ${alpha})`;
      ctx.fill();
    }
  }

  resize();
  window.addEventListener('resize', resize);

  const loop = (now) => {
    drawParticles(now);
    rafId = requestAnimationFrame(loop);
  };
  rafId = requestAnimationFrame(loop);

  // Pause when tab hidden, resume when visible
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      cancelAnimationFrame(rafId);
      rafId = null;
    } else if (!rafId) {
      rafId = requestAnimationFrame(loop);
    }
  });
}

function initParallax(root) {
  // Skip on reduced-motion or narrow viewport (mobile)
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.innerWidth <= 749) return;

  let ticking = false;

  function updateScrollY() {
    const scrollY = Math.min(window.scrollY || 0, 600);
    root.style.setProperty('--scroll-y', scrollY + 'px');
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(updateScrollY);
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  // Set initial value
  updateScrollY();
}

function detectWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext &&
      (c.getContext('webgl') || c.getContext('experimental-webgl')));
  } catch { return false; }
}

const SUN_VERT = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const SUN_FRAG = `
uniform float uTime;
varying vec2  vUv;

void main() {
  vec2  center = vUv - 0.5;
  float dist   = length(center);
  float angle  = atan(center.y, center.x);

  float core   = 1.0 - smoothstep(0.0,  0.08, dist);
  float halo   = 1.0 - smoothstep(0.08, 0.30, dist);
  float bloom  = (1.0 - smoothstep(0.30, 0.65, dist)) * 0.55;
  float corona = (1.0 - smoothstep(0.65, 1.0,  dist)) * 0.18;
  float pulse  = 1.0 + 0.04 * sin(uTime * 0.7);

  float rays = pow(max(0.0, cos(angle * 6.0)), 14.0) * 0.12
             * (1.0 - smoothstep(0.10, 0.85, dist));

  vec3 coreCol   = vec3(1.0, 1.0, 1.0);
  vec3 haloCol   = vec3(1.0, 0.97, 0.90);
  vec3 bloomCol  = mix(vec3(0.957, 0.780, 0.431), vec3(1.0, 0.494, 0.620), dist * 1.5);
  vec3 coronaCol = vec3(0.910, 0.302, 0.357);

  vec3 col = coreCol   * core
           + haloCol   * halo   * 0.85
           + bloomCol  * bloom  * pulse
           + coronaCol * corona * pulse
           + vec3(1.0, 0.95, 0.80) * rays;

  float alpha = clamp(core + halo * 0.75 + bloom + corona + rays, 0.0, 1.0);
  gl_FragColor = vec4(col, alpha);
}
`;

const WATER_VERT = `
uniform float uTime;
uniform float uSpeed;
varying float vElevation;
varying vec3  vNormal;
varying vec3  vWorldPos;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x),
    f.y
  );
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p *= 2.1;
    a *= 0.5;
  }
  return v;
}

void main() {
  float t  = uTime * uSpeed;
  vec2  xz = position.xz;

  float swell  = fbm(xz * 0.12 + vec2(t * 0.30, t * 0.20)) * 1.20;
  float chop   = fbm(xz * 0.35 + vec2(t * 0.80, t * 0.60)) * 0.45;
  float ripple = fbm(xz * 0.90 + vec2(t * 1.40, t * 1.10)) * 0.12;
  float elevation = swell + chop + ripple;
  vElevation = elevation;
  vWorldPos  = (modelMatrix * vec4(position.x, elevation, position.z, 1.0)).xyz;

  float eps = 0.1;
  vec2 xzL = xz - vec2(eps, 0.0); vec2 xzR = xz + vec2(eps, 0.0);
  vec2 xzD = xz - vec2(0.0, eps); vec2 xzU = xz + vec2(0.0, eps);
  float hL = fbm(xzL*0.12+vec2(t*0.30,t*0.20))*1.20 + fbm(xzL*0.35+vec2(t*0.80,t*0.60))*0.45 + fbm(xzL*0.90+vec2(t*1.40,t*1.10))*0.12;
  float hR = fbm(xzR*0.12+vec2(t*0.30,t*0.20))*1.20 + fbm(xzR*0.35+vec2(t*0.80,t*0.60))*0.45 + fbm(xzR*0.90+vec2(t*1.40,t*1.10))*0.12;
  float hD = fbm(xzD*0.12+vec2(t*0.30,t*0.20))*1.20 + fbm(xzD*0.35+vec2(t*0.80,t*0.60))*0.45 + fbm(xzD*0.90+vec2(t*1.40,t*1.10))*0.12;
  float hU = fbm(xzU*0.12+vec2(t*0.30,t*0.20))*1.20 + fbm(xzU*0.35+vec2(t*0.80,t*0.60))*0.45 + fbm(xzU*0.90+vec2(t*1.40,t*1.10))*0.12;
  vNormal = normalize(vec3(hL - hR, 2.0 * eps, hD - hU));

  gl_Position = projectionMatrix * modelViewMatrix *
                vec4(position.x, elevation, position.z, 1.0);
}
`;

const WATER_FRAG = `
uniform vec3  uSunPosition;
varying float vElevation;
varying vec3  vNormal;
varying vec3  vWorldPos;

vec3 waterColor(float e) {
  vec3 col = vec3(0.106, 0.020, 0.047);
  col = mix(col, vec3(0.239, 0.059, 0.125), smoothstep(0.15, 0.30, e));
  col = mix(col, vec3(0.545, 0.176, 0.290), smoothstep(0.30, 0.50, e));
  col = mix(col, vec3(0.831, 0.408, 0.478), smoothstep(0.50, 0.70, e));
  col = mix(col, vec3(1.000, 0.902, 0.655), smoothstep(0.80, 0.95, e));
  return col;
}

void main() {
  float e   = clamp(vElevation / 1.77, 0.0, 1.0);
  vec3  col = waterColor(e);

  vec3  toSun    = normalize(uSunPosition - vWorldPos);
  vec3  viewDir  = normalize(vec3(0.0, 1.0, 0.0));
  float specular = pow(max(dot(reflect(-toSun, vNormal), viewDir), 0.0), 64.0);
  col += vec3(1.0, 0.95, 0.82) * specular * 1.8;

  float foam = smoothstep(0.88, 1.0, e);
  col = mix(col, vec3(1.0, 0.96, 0.88), foam * 0.7);

  gl_FragColor = vec4(col, 1.0);
}
`;

function initSunMesh(THREE) {
  const sunGeo = new THREE.PlaneGeometry(6, 6);
  const sunMat = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
    },
    vertexShader:   SUN_VERT,
    fragmentShader: SUN_FRAG,
    transparent: true,
    depthWrite:  false,
    blending:    THREE.AdditiveBlending,
    side:        THREE.DoubleSide,
  });
  const sunMesh = new THREE.Mesh(sunGeo, sunMat);
  sunMesh.position.set(-4, 3.5, -10);
  return { sunMesh, sunMat };
}

function initWaterMesh(THREE, sunMesh) {
  const waterGeo = new THREE.PlaneGeometry(40, 20, 128, 64);
  waterGeo.rotateX(-Math.PI / 2);

  const waterMat = new THREE.ShaderMaterial({
    uniforms: {
      uTime:        { value: 0 },
      uSpeed:       { value: 1.0 },
      uSunPosition: { value: sunMesh.position },
    },
    vertexShader:   WATER_VERT,
    fragmentShader: WATER_FRAG,
    side: THREE.FrontSide,
  });

  const waterMesh = new THREE.Mesh(waterGeo, waterMat);
  waterMesh.position.set(0, 0, 0);

  return { waterMesh, waterMat };
}

function initThreeScene(root, THREE) {
  const sceneEl = root.querySelector('.printale-contact-river__scene');
  if (!sceneEl) return null;

  const scene    = new THREE.Scene();
  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const canvas = renderer.domElement;
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:1;';
  sceneEl.appendChild(canvas);
  root.setAttribute('data-three-active', '');

  // Camera
  const rect   = sceneEl.getBoundingClientRect();
  const aspect = rect.width / rect.height;
  const camera = new THREE.PerspectiveCamera(55, aspect, 0.1, 100);
  camera.position.set(0, 3, 6);
  camera.lookAt(0, 0, -2);
  renderer.setSize(rect.width, rect.height);

  // Sun mesh
  const { sunMesh, sunMat } = initSunMesh(THREE);
  scene.add(sunMesh);

  // Water mesh
  const { waterMesh, waterMat } = initWaterMesh(THREE, sunMesh);
  scene.add(waterMesh);

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) {
    waterMat.uniforms.uSpeed.value = 0.08;
  }

  // Clock
  const clock = new THREE.Clock();
  let threeRafId = null;

  // Animation loop
  function animate() {
    const delta = clock.getDelta();
    const timeIncrement = reduceMotion ? delta * 0.05 : delta;
    sunMat.uniforms.uTime.value += timeIncrement;
    waterMat.uniforms.uTime.value += timeIncrement;
    sunMesh.quaternion.copy(camera.quaternion);
    renderer.render(scene, camera);
    threeRafId = requestAnimationFrame(animate);
  }
  threeRafId = requestAnimationFrame(animate);

  // Pause Three.js loop when tab is hidden
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      cancelAnimationFrame(threeRafId);
      threeRafId = null;
      clock.stop();
    } else {
      clock.start();
      threeRafId = requestAnimationFrame(animate);
    }
  });

  // Resize
  const ro = new ResizeObserver(() => {
    const r = sceneEl.getBoundingClientRect();
    renderer.setSize(r.width, r.height);
    camera.aspect = r.width / r.height;
    camera.updateProjectionMatrix();
  });
  ro.observe(sceneEl);

  return { scene, camera, renderer, sunMesh, waterMesh, waterMat };
}

function initContactPage(root) {
  enhanceContactForm(root);
  initParallax(root);

  const useThree = window.innerWidth > 749 && detectWebGL();
  if (useThree) {
    import('three')
      .then(THREE => initThreeScene(root, THREE))
      .catch(() => {
        initRiverScene2D(root);
        initParticles(root);
      });
  } else {
    initRiverScene2D(root);
    initParticles(root);
  }
}

function boot() {
  document.querySelectorAll(pageSelector).forEach(initContactPage);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
